import { useState } from 'react';
import jsPDF from 'jspdf';

/**
 * Custom hook para generar PDFs de cartas de solicitud de vacaciones
 * @returns {Object} { generatePDF, isGenerating }
 */
const useVacationLetterPDF = () => {
    const [isGenerating, setIsGenerating] = useState(false);

    /**
     * Genera y descarga el PDF de la carta de vacaciones
     * @param {Object} vacationData - Datos de la solicitud de vacaciones
     * @param {string} vacationData.employeeName - Nombre completo del empleado
     * @param {Date|string} vacationData.startDate - Fecha de inicio
     * @param {Date|string} vacationData.endDate - Fecha de fin
     * @param {number} vacationData.totalDays - Total de días solicitados
     * @param {Array} vacationData.specificDates - Array de fechas específicas (opcional)
     */
    const generatePDF = (vacationData) => {
        try {
            setIsGenerating(true);

            const {
                employeeName,
                startDate,
                endDate,
                totalDays,
                specificDates = null
            } = vacationData;

            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'letter'
            });

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(12);

            let yPosition = 30;
            const lineHeight = 5;
            const pageWidth = 210;
            const marginRight = 20;
            const textWidth = 170;

            // Funciones auxiliares
            const addRightAlignedText = (text, y, fontSize = 12, fontWeight = 'normal') => {
                doc.setFontSize(fontSize);
                doc.setFont('helvetica', fontWeight);
                const textWidthCalc = doc.getTextWidth(text);
                const x = pageWidth - marginRight - textWidthCalc;
                doc.text(text, x, y);
            };

            const addJustifiedText = (text, x, y, maxWidth, lineHeight) => {
                const words = text.split(' ');
                let line = '';
                let currentY = y;

                for (let i = 0; i < words.length; i++) {
                    const testLine = line + (line ? ' ' : '') + words[i];
                    const testWidth = doc.getTextWidth(testLine);

                    if (testWidth > maxWidth && line !== '') {
                        if (i < words.length - 1) {
                            justifyLine(line, x, currentY, maxWidth);
                        } else {
                            doc.text(line, x, currentY);
                        }
                        line = words[i];
                        currentY += lineHeight;
                    } else {
                        line = testLine;
                    }
                }

                doc.text(line, x, currentY);
                return currentY + lineHeight;
            };

            const justifyLine = (line, x, y, maxWidth) => {
                const words = line.split(' ');
                if (words.length === 1) {
                    doc.text(line, x, y);
                    return;
                }

                const totalWidth = doc.getTextWidth(line);
                const spaceWidth = doc.getTextWidth(' ');
                const extraSpace = (maxWidth - totalWidth) / (words.length - 1);

                let currentX = x;
                for (let i = 0; i < words.length; i++) {
                    doc.text(words[i], currentX, y);
                    currentX += doc.getTextWidth(words[i]);
                    if (i < words.length - 1) {
                        currentX += spaceWidth + extraSpace;
                    }
                }
            };

            // Encabezado
            addRightAlignedText('El Cenidor, Municipio de Múgica, Michoacán, México.', yPosition);
            yPosition += lineHeight;

            const fechaActual = new Date();
            const fechaFormateada = fechaActual.toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
            addRightAlignedText(`A ${fechaFormateada}`, yPosition);
            yPosition += lineHeight * 2;

            addRightAlignedText('ASUNTO: SOLICITUD DE VACACIONES', yPosition, 12, 'bold');
            yPosition += lineHeight * 2;

            // Destinatario
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('ING. ALFONSO CALDERÓN CHÁVEZ', 20, yPosition);
            yPosition += lineHeight;
            doc.text('DIRECTOR GENERAL', 20, yPosition);
            yPosition += lineHeight;
            doc.text('AKASIA SOFTWARE DEVELOPMENT SA DE CV', 20, yPosition);
            yPosition += lineHeight * 2;

            doc.setFont('helvetica', 'normal');

            // Formatear fechas
            const nombreCompleto = employeeName.toUpperCase();
            
            const fechaInicio = new Date(startDate);
            const fechaInicioFormateada = fechaInicio.toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });

            const fechaFin = new Date(endDate);
            const fechaFinFormateada = fechaFin.toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });

            // Texto principal
            let textoPrincipal;
            if (totalDays === 1) {
                textoPrincipal = `Me dirijo a usted para solicitar formalmente vacaciones correspondientes al día ${fechaInicioFormateada}.`;
            } else {
                textoPrincipal = `Me dirijo a usted para solicitar formalmente vacaciones correspondientes al periodo comprendido del ${fechaInicioFormateada} al ${fechaFinFormateada}, con un total de ${totalDays} día${totalDays > 1 ? 's' : ''} solicitados.`;
            }
            
            yPosition = addJustifiedText(textoPrincipal, 20, yPosition, textWidth, lineHeight) + lineHeight;

            // Agregar lista de fechas específicas si existen
            if (specificDates && specificDates.length > 0) {
                yPosition += lineHeight * 0.5;
                
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.text('Días específicos solicitados:', 20, yPosition);
                yPosition += lineHeight;
                
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                
                const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
                const diasSemana = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
                const fechasFormateadas = specificDates.map(fecha => {
                    const fechaDate = new Date(fecha.dateString + 'T00:00:00');
                    const dia = fechaDate.getDate();
                    const mes = meses[fechaDate.getMonth()];
                    const diaSemana = diasSemana[fechaDate.getDay()];
                    const anio = fechaDate.getFullYear();
                    return `${diaSemana}. ${dia} ${mes}. ${anio}`;
                });
                
                const fechasPorLinea = 3;
                for (let i = 0; i < fechasFormateadas.length; i += fechasPorLinea) {
                    const chunk = fechasFormateadas.slice(i, i + fechasPorLinea);
                    const linea = '  ' + chunk.join('  |  ');
                    doc.text(linea, 25, yPosition);
                    yPosition += lineHeight * 0.9;
                    
                    if (yPosition > 250) {
                        doc.addPage();
                        yPosition = 30;
                    }
                }
                
                doc.setFontSize(12);
                yPosition += lineHeight;
            }

            // Textos adicionales
            const texto1 = 'Durante mi ausencia, me aseguraré de que todas mis responsabilidades y tareas sean debidamente cubiertas y delegadas. Entiendo la importancia de proporcionar un servicio continuo y de calidad a nuestros clientes.';
            yPosition = addJustifiedText(texto1, 20, yPosition, textWidth, lineHeight) + lineHeight;

            const texto2 = 'Si hay alguna consideración o requisito específico que deba tener en cuenta al programar mis vacaciones, le agradecería que me lo informara lo antes posible.';
            yPosition = addJustifiedText(texto2, 20, yPosition, textWidth, lineHeight) + lineHeight;

            const texto3 = 'Agradezco su atención y consideración en relación con mi solicitud de vacaciones. Valorizo la oportunidad de tomarme un tiempo para descansar y recargar energías, lo cual, a su vez, me permitirá regresar al trabajo con renovada dedicación y brindar un mejor servicio a nuestros clientes.';
            yPosition = addJustifiedText(texto3, 20, yPosition, textWidth, lineHeight) + lineHeight;

            const texto4 = 'Quedo a su disposición para cualquier consulta o discusión adicional relacionada con mi solicitud. Agradezco su pronta respuesta.';
            yPosition = addJustifiedText(texto4, 20, yPosition, textWidth, lineHeight) + lineHeight * 2;

            // Cierre
            const cierreYPosition = yPosition;
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(12);
            doc.text('Atentamente,', 20, cierreYPosition);
            
            const voBoPosX = 110;
            doc.text('Vo. Bo.', voBoPosX, cierreYPosition);
            
            yPosition = cierreYPosition + lineHeight * 4;

            // Firmas
            const firmaYPosition = yPosition;
            
            doc.line(20, firmaYPosition, 100, firmaYPosition);
            doc.setFont('helvetica', 'bold');
            doc.text(nombreCompleto, 20, firmaYPosition + lineHeight);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text('Empleado', 20, firmaYPosition + lineHeight * 2);
            
            const firmaDerechaX = 110;
            doc.line(firmaDerechaX, firmaYPosition, firmaDerechaX + 80, firmaYPosition);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('ING. ALFONSO CALDERON CHAVEZ', firmaDerechaX, firmaYPosition + lineHeight);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text('Director General Akasia Software Development', firmaDerechaX, firmaYPosition + lineHeight * 2);

            // Descargar el PDF
            const nombreArchivo = `Solicitud_Vacaciones_${employeeName.replace(/\s+/g, '_')}_${totalDays}dias_${fechaInicio.toISOString().split('T')[0]}.pdf`;
            doc.save(nombreArchivo);

            return true;

        } catch (error) {
            console.error('Error al generar el PDF:', error);
            throw new Error('Error al generar el documento PDF. Por favor intente nuevamente.');
        } finally {
            setIsGenerating(false);
        }
    };

    return {
        generatePDF,
        isGenerating
    };
};

export default useVacationLetterPDF;
